import axios, { AxiosInstance } from "axios";
import { addfiles } from "../../config/variables";
import { ProjectDataProps } from "../../Components/common/types";
import { errLog, log } from "../logger";
import { getErrorMessage } from "../errors";

// Define UploadResultItem as before
export interface UploadResultItem {
  url: string;
  publicId: string;
  resourceType: string;
  fileSignature: string;
  status: string;
  isCover: boolean;
}

// Define the shape of the full upload response
interface UploadResponse {
  success: boolean;
  message: string;
  duplicatesFound?: boolean;
  results: UploadResultItem[];
}

// Updated function
export const uploadFiles = async (
  files: File[],
  projectName: string,
  api: AxiosInstance,
  isEditing: boolean = false
): Promise<UploadResultItem[]> => {
  const formData = new FormData();
  formData.append("projectName", projectName);
  files.forEach((file) => formData.append("files", file));

  try {
    const response = await api.post<UploadResponse>(addfiles, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "x-is-edit": isEditing,
      },
    });

    const data = response.data;

    if (!Array.isArray(data.results)) {
      throw new Error(
        "Invalid response: results field is missing or not an array"
      );
    }

    return data.results;
  } catch (error) {
    errLog("File upload failed:", getErrorMessage(error));

    //check this during editing
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      const serverMsg =
        error.response.data?.message || "All files were duplicates.";
      errLog("File upload failed:", serverMsg);
      throw new Error(serverMsg);
    }
    throw new Error("Upload failed");
    //a case of 409, conflict, an error will be thrown, this is good just incase,
    //  but the front end takes care of this so no duplicates will be allowed
  }
};

export async function syncUploadedFiles(
  uploadResults: UploadResultItem[],
  projectData: ProjectDataProps
) {

  log("Upload result", uploadResults);
  //Incase duplicates files were selected but will not happen since front end prevents this, 
  // this prevents syncing since the result from file upload will be missing some files data after duplicates are skipped
  const totalFiles =
    projectData.galleryData.imgData.length +
    projectData.galleryData.vidData.length;

  if (uploadResults.length !== totalFiles) {
    throw new Error(
      "Upload mismatch: some files were skipped (likely duplicates)."
    );
  }

  const updatedImgData = projectData.galleryData.imgData.map((img) => {
    const match = uploadResults.find(
      (res) => res.fileSignature === img.fileSignature
    );
    return match
      ? {
          ...img,
          src: match.url,
          publicId: match.publicId,
          isCover: match.isCover ?? false,
        }
      : img;
  });

  const updatedVidData = projectData.galleryData.vidData.map((vid) => {
    const match = uploadResults.find(
      (res) => res.fileSignature === vid.fileSignature
    );
    return match
      ? {
          ...vid,
          src: match.url,
          publicId: match.publicId,
          isCover: match.isCover ?? false,
        }
      : vid;
  });

   return {
    ...projectData,
    galleryData: {
      imgData: updatedImgData,
      vidData: updatedVidData,
    },
  };

}
